import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'

// Define types directly to avoid Database type issues
type AccountPipeline = {
  id: string
  account_id: string
  name: string
  description: string | null
  is_active: boolean | null
  created_at: string | null
  updated_at: string | null
}

type AccountPipelineStage = {
  id: string
  account_id: string
  pipeline_id: string
  name: string
  order_index: number | null
  is_active: boolean | null
  created_at: string | null
  updated_at: string | null
  stage_type: 'normal' | 'won' | 'lost'
}

interface UsePipelinesReturn {
  pipelines: AccountPipeline[]
  pipelineStages: AccountPipelineStage[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

export const usePipelines = (): UsePipelinesReturn => {
  const { user } = useAuth()
  const [pipelines, setPipelines] = useState<AccountPipeline[]>([])
  const [pipelineStages, setPipelineStages] = useState<AccountPipelineStage[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchPipelines = async (): Promise<void> => {
    try {
      setLoading(true)
      setError(null)

      if (!user || !supabase) {
        setLoading(false)
        return
      }

      // Buscar pipelines
      const { data: pipelinesData, error: pipelinesError } = await supabase
        .from('account_pipelines')
        .select('*')
        .eq('account_id', user.account_id)
        .eq('is_active', true)
        .order('name')

      if (pipelinesError) {
        throw new Error(`Erro ao buscar pipelines: ${pipelinesError.message}`)
      }

      // Buscar stages dos pipelines
      const { data: stagesData, error: stagesError } = await supabase
        .from('account_pipeline_stages')
        .select('*')
        .eq('account_id', user.account_id)
        .eq('is_active', true)
        .order('order_index')

      if (stagesError) {
        throw new Error(`Erro ao buscar etapas: ${stagesError.message}`)
      }

      setPipelines(pipelinesData || [])
      setPipelineStages(stagesData || [])
    } catch (err) {
      console.error('💥 [usePipelines] Erro:', err)
      setError(err instanceof Error ? err.message : 'Erro desconhecido')
    } finally {
      setLoading(false)
    }
  }

  const refetch = async (): Promise<void> => {
    await fetchPipelines()
  }

  useEffect(() => {
    if (user) {
      fetchPipelines()
    }
  }, [user])

  return {
    pipelines,
    pipelineStages,
    loading,
    error,
    refetch,
  }
}